import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { useCart } from '../contexts/CartContext';

const CartDrawer = ({ children }) => {
  const { cart, updateQuantity, removeFromCart, getCartSubtotal, getCartVAT, getCartTotalWithVAT, getCartCount } = useCart();

  return (
    <Sheet>
      <SheetTrigger asChild>
        {children}
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={24} />
            Carrello ({getCartCount()} articoli)
          </SheetTitle>
        </SheetHeader>

        <div className="mt-8">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={64} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-4">Il tuo carrello è vuoto</p>
              <Link to="/prodotti">
                <Button>Inizia ad acquistare</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {cart.map((item) => {
                  const itemPrice = item.price || 0;
                  const discount = item.discount_percentage || 0;
                  const finalPrice = itemPrice * (1 - discount / 100);
                  const quantityMultiplier = item.unit_type === 'bancali' ? 54 : 1;
                  const totalSacchi = item.quantity * quantityMultiplier;

                  return (
                    <div key={`${item.product_id}-${item.unit_type}`} className="flex gap-4 p-4 border rounded-lg">
                      <img
                        src={item.image_url || 'https://via.placeholder.com/80'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800">{item.name}</h4>
                        <p className="text-sm text-slate-600">{item.brand}</p>
                        
                        {item.unit_type === 'bancali' && (
                          <p className="text-xs text-blue-600 font-semibold mt-1">
                            {item.quantity} Bancali ({totalSacchi} sacchi totali)
                          </p>
                        )}
                        
                        {item.show_price ? (
                          <div className="mt-2">
                            {discount > 0 && (
                              <span className="text-xs text-slate-400 line-through mr-2">
                                €{(itemPrice * quantityMultiplier).toFixed(2)}
                              </span>
                            )}
                            <span className="text-lg font-bold text-orange-600">
                              €{(finalPrice * quantityMultiplier).toFixed(2)}
                            </span>
                            <span className="text-xs text-slate-600">
                              {item.unit_type === 'bancali' ? '/bancale' : '/sacco'}
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm text-orange-600 mt-2">Prezzo su richiesta</p>
                        )}

                        <div className="flex items-center gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <Minus size={14} />
                          </Button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            disabled={item.quantity >= item.quantity_in_stock}
                          >
                            <Plus size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-auto text-red-600 hover:bg-red-50"
                            onClick={() => removeFromCart(item.product_id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between items-center text-slate-700">
                  <span>Subtotale (IVA esclusa):</span>
                  <span className="font-semibold">
                    €{getCartSubtotal().toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-slate-700">
                  <span>IVA (22%):</span>
                  <span className="font-semibold">
                    €{getCartVAT().toFixed(2)}
                  </span>
                </div>
                <div className="border-t-2 border-slate-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Totale IVA inclusa:</span>
                    <span className="text-2xl font-bold text-orange-600">
                      €{getCartTotalWithVAT().toFixed(2)}
                    </span>
                  </div>
                </div>
                <Link to="/checkout" className="block mt-4">
                  <Button className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white">
                    Procedi alla Richiesta d'Ordine
                  </Button>
                </Link>
                
                {/* Messaggio per chiudere il carrello */}
                <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-sm text-blue-800 flex items-center gap-2">
                    <X size={16} className="text-blue-600" />
                    <span>Clicca sulla <strong>X</strong> in alto a destra per continuare con la conferma d'ordine</span>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
